import {
  BlockTypeSelect,
  BoldItalicUnderlineToggles,
  CreateLink,
  diffSourcePlugin,
  DiffSourceToggleWrapper,
  headingsPlugin,
  linkDialogPlugin,
  listsPlugin,
  ListsToggle,
  MDXEditor,
  quotePlugin,
  Separator,
  tablePlugin,
  thematicBreakPlugin,
  toolbarPlugin,
  UndoRedo
} from '@mdxeditor/editor';
import '@mdxeditor/editor/style.css';
import { Handle, Position } from "@xyflow/react";
import { v4 as uuidv4 } from "uuid";
import styles from "./SurveyNode.module.css";

const cardStyle = {
  border: "1px solid #E5E7EB",
  borderRadius: 8,
  background: "#fff",
  minWidth: 220,
  width: "max-content",
  boxShadow: "0 1px 2px rgba(0,0,0,0.06)",
  overflow: "hidden",
  fontFamily: "Inter, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif",
};

const headerStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "8px 10px",
  background: "#F9FAFB",
  borderBottom: "1px solid #F3F4F6",
  fontSize: 12,
  color: "#374151",
  fontWeight: 600,
};

const bodyStyle = {
  padding: 10,
  fontSize: 13,
  color: "#111827",
};

export default function SurveyNode({ id, data, selected }) {
  const {
    title = "Blocco",
    content,
    onChange,
    questionType = "Open",
    options = [],
    min = 1,
    max = 5,
    step = 1,
    text = "",
    required = true,
    maxSelections = null,
    minSelections = null,
    minLabel = '',
    maxLabel = '',
    validation = '',
  } = data || {};

  const isSingleChoice = questionType === "SingleChoice";
  const isMultipleChoice = questionType === "MultipleChoice";

  const addOption = () => {
    const newOpts = [...options, { id: uuidv4(), value: "Opzione" }];
    onChange?.(id, { options: newOpts });
  };

  const updateOption = (idx, value) => {
    const newOpts = options.map((o, i) => (i === idx ? { ...o, value } : o));
    onChange?.(id, { options: newOpts });
  };

  const updateOptionType = (idx, type) => {
    const newOpts = options.map((o, i) => (i === idx ? { ...o, type } : o));
    onChange?.(id, { options: newOpts });
  };

  const removeOption = (idx) => {
    const newOpts = options.filter((_, i) => i !== idx);
    onChange?.(id, { options: newOpts });
  };

  const outerStyle = {
    ...cardStyle,
    border: `1px solid ${selected ? '#6366F1' : '#E5E7EB'}`,
    boxShadow: selected
      ? '0 0 0 2px rgba(99,102,241,0.18), 0 2px 6px rgba(0,0,0,0.08)'
      : cardStyle.boxShadow,
  };

  const topHandleColor = selected ? '#6366F1' : '#9CA3AF';
  const defaultHandleColor = selected ? '#6366F1' : '#10B981';

  return (
    <div style={outerStyle}>
      {/* connection handles */}
      <Handle type="target" position={Position.Top} style={{ background: topHandleColor, width: 8, height: 8 }} />

      <div style={headerStyle}>
        <span>{title}</span>
      </div>

      <div style={bodyStyle}>
        {/* If content (custom component) is provided, render it; otherwise the survey editor fields */}
        {content ? (
          content
        ) : (
          <DefaultInner
            id={id}
            onChange={onChange}
            questionType={questionType}
            options={options}
            group={data?.group ?? ''}
            showOptions={isSingleChoice || isMultipleChoice}
            hasPerOptionHandles={isSingleChoice}
            addOption={addOption}
            updateOption={updateOption}
            updateOptionType={updateOptionType}
            removeOption={removeOption}
            min={min}
            max={max}
            step={step}
            text={text}
            required={required}
            maxSelections={maxSelections}
            minSelections={minSelections}
            minLabel={minLabel}
            maxLabel={maxLabel}
            validation={validation}
            selected={selected}
          />
        )}
      </div>

      {/* Default/else path handle at bottom (available for all types) */}
      <Handle type="source" id="default" position={Position.Bottom} style={{ background: defaultHandleColor, width: 8, height: 8 }} />
    </div>
  );
}

function DefaultInner({
  id,
  onChange,
  questionType,
  options,
  showOptions,
  hasPerOptionHandles,
  addOption,
  updateOption,
  removeOption,
  updateOptionType,
  min,
  max,
  step,
  text,
  required,
  group,
  maxSelections,
  minSelections,
  minLabel,
  maxLabel,
  validation,
  selected,
}) {
  const optionHandleColor = selected ? '#6366F1' : '#10B981';
  const stop = (e) => e.stopPropagation();
  return (
    <div style={{ display: "grid", gap: 10 }}>
      <div style={{ display: "grid", gap: 6 }}>
        <label style={{ fontSize: 12, color: "#6B7280" }}>Testo domanda</label>
        <div className={`${styles.editor} nodrag nopan`}>
          <MDXEditor
            className={`${styles.markdown} nodrag nopan`}
            markdown={text}
            onChange={(md) => onChange?.(id, { text: md })}
            plugins={[
              headingsPlugin(),
              listsPlugin(),
              quotePlugin(),
              thematicBreakPlugin(),
              linkDialogPlugin(),
              diffSourcePlugin(),
              tablePlugin(),
              toolbarPlugin({
                toolbarContents: () => (
                  <>
                    {' '}
                    <UndoRedo />
                    <Separator />
                    <BoldItalicUnderlineToggles />
                    <Separator />
                    <ListsToggle />
                    <CreateLink />
                    {/* <InsertTable /> */}
                    <Separator />
                    <BlockTypeSelect />
                    <DiffSourceToggleWrapper options={["rich-text", "source"]} />
                  </>
                )
              })
            ]}
          />
        </div>
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <input
          id={`req-${id}`}
          type="checkbox"
          checked={!!required}
          onChange={(e) => onChange?.(id, { required: e.target.checked })}
          className="nodrag nopan"
          onPointerDown={stop}
          onKeyDown={stop}
        />
        <label htmlFor={`req-${id}`} style={{ fontSize: 12, color: "#374151" }}>Obbligatoria</label>
      </div>

      <div style={{ display: "grid", gap: 6 }}>
        <label style={{ fontSize: 12, color: "#6B7280" }}>Sezione</label>
        <input
          type="text"
          value={group || ''}
          onChange={(e) => onChange?.(id, { group: e.target.value })}
          placeholder="Es. Introduzione, Feedback, ..."
          className="nodrag nopan"
          onPointerDown={stop}
          onKeyDown={stop}
          style={{ border: "1px solid #E5E7EB", borderRadius: 6, padding: "6px 8px", fontSize: 13 }}
        />
      </div>

      <div style={{ display: "grid", gap: 6 }}>
        <label style={{ fontSize: 12, color: "#6B7280" }}>Tipo domanda</label>
        <select
          value={questionType}
          onChange={(e) => onChange?.(id, { questionType: e.target.value })}
          className="nodrag nopan"
          onPointerDown={stop}
          onKeyDown={stop}
          style={{ border: "1px solid #E5E7EB", borderRadius: 6, padding: "6px 8px", fontSize: 13 }}
        >
          <option value="Open">Risposta aperta</option>
          <option value="SingleChoice">Risposta singola</option>
          <option value="MultipleChoice">Risposta multipla</option>
          <option value="Numeric">Valutazione (numerica)</option>
        </select>
      </div>

      {questionType === "Numeric" && (
        <div style={{ display: "grid", gap: 6 }}>
          <div style={{ display: "flex", flexDirection: 'column', gap: 6 }}>
            <label style={{ fontSize: 12, color: "#6B7280" }}>Minimo</label>
            <input
              type="number"
              value={min}
              onChange={(e) => onChange?.(id, { min: Number(e.target.value) })}
              placeholder="Min"
              className="nodrag nopan"
              onPointerDown={stop}
              onKeyDown={stop}
              style={{ border: "1px solid #E5E7EB", borderRadius: 6, padding: "6px 8px", fontSize: 13 }}
            />
            <label style={{ fontSize: 12, color: "#6B7280" }}>Massimo</label>

            <input
              type="number"
              value={max}
              onChange={(e) => onChange?.(id, { max: Number(e.target.value) })}
              placeholder="Max"
              className="nodrag nopan"
              onPointerDown={stop}
              onKeyDown={stop}
              style={{ border: "1px solid #E5E7EB", borderRadius: 6, padding: "6px 8px", fontSize: 13 }}
            />
            <label style={{ fontSize: 12, color: "#6B7280" }}>Step</label>

            <input
              type="number"
              value={step}
              onChange={(e) => onChange?.(id, { step: Number(e.target.value) })}
              placeholder="Step"
              className="nodrag nopan"
              onPointerDown={stop}
              onKeyDown={stop}
              style={{ border: "1px solid #E5E7EB", borderRadius: 6, padding: "6px 8px", fontSize: 13 }}
            />
          </div>
          <div style={{ display: 'grid', gap: 6 }}>
            <label style={{ fontSize: 12, color: '#6B7280' }}>Etichetta minimo</label>
            <input
              type="text"
              value={minLabel || ''}
              onChange={(e) => onChange?.(id, { minLabel: e.target.value })}
              placeholder="Es. Estremamente insoddisfatto"
              className="nodrag nopan"
              onPointerDown={stop}
              onKeyDown={stop}
              style={{ border: '1px solid #E5E7EB', borderRadius: 6, padding: '6px 8px', fontSize: 13 }}
            />
            <label style={{ fontSize: 12, color: '#6B7280' }}>Etichetta massimo</label>
            <input
              type="text"
              value={maxLabel || ''}
              onChange={(e) => onChange?.(id, { maxLabel: e.target.value })}
              placeholder="Es. Estremamente soddisfatto"
              className="nodrag nopan"
              onPointerDown={stop}
              onKeyDown={stop}
              style={{ border: '1px solid #E5E7EB', borderRadius: 6, padding: '6px 8px', fontSize: 13 }}
            />
          </div>
          {/* <small style={{ color: "#9CA3AF" }}>Crea una sola uscita "default". Le regole/ramificazioni numeriche possono essere aggiunte in seguito.</small> */}
        </div>
      )}

      {showOptions && (
        <div style={{ display: "grid", gap: 8 }}>
          <label style={{ fontSize: 12, color: "#6B7280" }}>Opzioni</label>
          {options.map((opt, idx) => (
            <div
              key={opt.id}
              style={{ display: "grid", gridTemplateColumns: "auto 1fr auto", gap: 10, alignItems: "center", position: "relative", paddingRight: 22 }}
            >
              {/* Move delete button before option input */}
              <button
                onClick={() => removeOption(idx)}
                style={{
                  border: "1px solid #FCA5A5",
                  color: "#B91C1C",
                  background: "#FEF2F2",
                  borderRadius: 6,
                  padding: "6px 8px",
                  fontSize: 12,
                  cursor: "pointer",
                }}
                className="nodrag nopan"
                onPointerDown={stop}
                onKeyDown={stop}
                title="Rimuovi opzione"
              >
                ×
              </button>
              <input
                value={opt.value}
                onChange={(e) => updateOption(idx, e.target.value)}
                placeholder={`Opzione ${idx + 1}`}
                className="nodrag nopan"
                onPointerDown={stop}
                onKeyDown={stop}
                style={{ border: "1px solid #E5E7EB", borderRadius: 6, padding: "6px 8px", fontSize: 13 }}
              />
              {/* Toggle for per-option Open type */}
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#374151' }} className="nodrag nopan" onPointerDown={stop} onKeyDown={stop}>
                <input
                  type="checkbox"
                  checked={opt.type === 'open'}
                  onChange={(e) => updateOptionType(idx, e.target.checked ? 'open' : undefined)}
                  className="nodrag nopan"
                  onPointerDown={stop}
                  onKeyDown={stop}
                />
                Open
              </label>
              {/* Per-opzione handle solo per domande a risposta singola */}
              {hasPerOptionHandles && (
                <Handle
                  type="source"
                  id={`opt-${opt.id}`}
                  position={Position.Right}
                  style={{
                    background: optionHandleColor,
                    width: 12,
                    height: 12,
                    right: 4,
                    top: "50%",
                    transform: "translateY(-50%)",
                    zIndex: 2,
                  }}
                />
              )}
            </div>
          ))}
          <div>
            <button
              onClick={addOption}
              style={{
                border: "1px solid #D1FAE5",
                color: "#065F46",
                background: "#ECFDF5",
                borderRadius: 6,
                padding: "6px 8px",
                fontSize: 12,
                cursor: "pointer",
              }}
              className="nodrag nopan"
              onPointerDown={stop}
              onKeyDown={stop}
            >
              + Aggiungi opzione
            </button>
          </div>
          <small style={{ color: "#9CA3AF" }}>Trascina un collegamento dalla maniglia a destra di ogni opzione verso il prossimo nodo.</small>
        </div>
      )}

      {questionType === 'MultipleChoice' && (
        <div style={{ display: 'grid', gap: 6 }}>
          <label style={{ fontSize: 12, color: '#6B7280' }}>Vincoli selezione (opzionale)</label>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            <input
              type="number"
              min={0}
              value={minSelections ?? ''}
              onChange={(e) => {
                const { value } = e.target;
                const parsed = value === '' ? null : Number(value);
                onChange?.(id, { minSelections: Number.isNaN(parsed) ? null : parsed });
              }}
              placeholder="Minime"
              className="nodrag nopan"
              onPointerDown={stop}
              onKeyDown={stop}
              style={{ border: '1px solid #E5E7EB', borderRadius: 6, padding: '6px 8px', fontSize: 13 }}
            />
            <input
              type="number"
              min={1}
              value={maxSelections ?? ''}
              onChange={(e) => {
                const { value } = e.target;
                const parsed = value === '' ? null : Number(value);
                onChange?.(id, { maxSelections: Number.isNaN(parsed) ? null : parsed });
              }}
              placeholder="Massime"
              className="nodrag nopan"
              onPointerDown={stop}
              onKeyDown={stop}
              style={{ border: '1px solid #E5E7EB', borderRadius: 6, padding: '6px 8px', fontSize: 13 }}
            />
          </div>
          <small style={{ color: '#9CA3AF' }}>Lascia vuoto per non applicare il limite.</small>
        </div>
      )}

      {questionType === 'Open' && (
        <div style={{ display: 'grid', gap: 6 }}>
          <label style={{ fontSize: 12, color: '#6B7280' }}>Validazione risposta (opzionale)</label>
          <select
            value={validation || ''}
            onChange={(e) => onChange?.(id, { validation: e.target.value || '' })}
            className="nodrag nopan"
            onPointerDown={stop}
            onKeyDown={stop}
            style={{ border: '1px solid #E5E7EB', borderRadius: 6, padding: '6px 8px', fontSize: 13 }}
          >
            <option value="">Nessuna</option>
            <option value="email">Email</option>
            <option value="phone">Telefono</option>
            <option value="city">Comune (lista)</option>
          </select>
        </div>
      )}
    </div>
  );
}
